import type { UploadDateRange } from "./types";
/**
 * Timeline Repository Types
 *
 * Types for server-side aggregation of document timeline data.
 * Supports efficient loading of 6000+ documents with lazy loading.
 */

import type { DocumentRowMapped } from "./types";

/**
 * A time bucket from the server-side aggregation.
 * Contains count and metadata, NOT individual documents.
 */
export interface TimelineBucket {
  bucketStart: Date;
  bucketEnd: Date;
  documentCount: number;
  earliestDocId: number;
  latestDocId: number;
}

/**
 * Granularity for time bucketing.
 * Maps to PostgreSQL date_trunc intervals.
 */
export type TimelineGranularity = "year" | "month" | "week" | "day";

/**
 * Which date places a document on the Timeline.
 * - `file` — `effective_modified_at`: the creation/modification date in the
 *   file's own metadata, else its upload date. The default, and the only basis
 *   before franklin-data-pipeline 20261016102236.
 * - `upload` — `created_at`: when the document reached Franklin.
 */
export type TimelineDateBasis = "file" | "upload";

/**
 * Filter parameters for timeline queries.
 * These are applied at the database level before aggregation/pagination.
 */
export interface TimelineFilterParams {
  /** Filter by project IDs (OR logic) */
  projectIds?: number[];
  /** Filter by document category IDs (OR logic, M2M) */
  categoryIds?: number[];
  /** Filter by design discipline IDs (OR logic, M2M) */
  designDisciplineIds?: number[];
  /** Filter by design stage IDs (OR logic, M2M) */
  designStageIds?: number[];
  /** Search filename (ILIKE) */
  searchText?: string;
  /** Filter by folder ID. Use null for root folder, undefined for all folders */
  folderId?: string | null;
  /** When true, keep only documents carrying the Verified mark. */
  verifiedOnly?: boolean;
  /** Which date buckets, pages and places rows. Omitted means `file`. */
  dateBasis?: TimelineDateBasis;
  /** When documents reached Franklin, whatever the date basis (pipeline #377). */
  uploadDateRange?: UploadDateRange;
}

/**
 * Parameters for fetching timeline summary (bucketed counts).
 */
export interface TimelineSummaryParams extends TimelineFilterParams {
  buildingId: string;
  granularity: TimelineGranularity;
}

/**
 * Parameters for fetching documents in a time window.
 * Supports cursor-based pagination for efficient scrolling.
 */
export interface TimelineWindowParams extends TimelineFilterParams {
  buildingId: string;
  startTime: Date;
  endTime: Date;
  limit?: number;
  /** Cursor for pagination - timestamp of last fetched document */
  cursor?: {
    timestamp: Date;
    id: number;
  };
  /**
   * How many rows of this window the caller already holds. Read when the cursor
   * is rejected as stale, and when no cursor is given at all — either way the
   * read pages forward until it has at least this many rows back, so recovery
   * never collapses a deeply-scrolled window to its first page. Omit it (or 0)
   * to read page one alone.
   */
  loadedRows?: number;
}

/**
 * Result of fetching documents in a time window.
 * Includes pagination metadata.
 */
export interface TimelineWindowResult {
  documents: DocumentRowMapped[];
  totalCount: number;
  hasMore: boolean;
  /** Cursor for next page (null if no more) */
  cursor: { timestamp: Date; id: number } | null;
  /**
   * True when the cursor sent was rejected as stale (its row had been hard
   * deleted) and the window was re-read from the first page instead. These
   * documents are the window read from the start — up to `loadedRows` of them,
   * not the page after the cursor — so a caller paging must REPLACE what it
   * holds with them, never append.
   */
  restarted?: boolean;
  /**
   * True when a restart could not page back to the depth the caller had: a
   * refill page's own cursor was rejected too, so the window is closed at the
   * rows recovered (`hasMore: false`, no cursor, `totalCount` capped at them).
   *
   * The rest of the window is NOT gone — reading it again from a fresh cursor
   * still returns it. So this is the difference between "you have seen
   * everything" and "we gave up here", and a caller that renders a list must
   * offer the retry rather than end it silently.
   */
  truncated?: boolean;
}

/**
 * Result of fetching timeline summary.
 * Contains bucketed counts for efficient overview rendering.
 */
export interface TimelineSummaryResult {
  buckets: TimelineBucket[];
  totalDocuments: number;
  timeRange: { start: Date; end: Date };
}

/**
 * State for a single expanded cluster.
 * Tracks documents loaded, pagination state, and loading status.
 */
export interface ExpandedClusterState {
  startTime: Date;
  endTime: Date;
  documents: DocumentRowMapped[];
  totalCount: number;
  hasMore: boolean;
  cursor: { timestamp: Date; id: number } | null;
  isLoading: boolean;
  /**
   * True when paging gave up short of the window's end (see
   * `TimelineWindowResult.truncated`). `hasMore` is false because this cursor
   * cannot go on — the rows are still there, and loading more re-reads the
   * window from scratch — so the view must show the retry, not the end.
   */
  truncated: boolean;
}

/**
 * The database predates the upload date basis (franklin-data-pipeline
 * 20261016102236): the timeline RPCs have no `p_date_basis`. The Timeline falls
 * back to the file-date basis and says why, rather than failing to load.
 */
export class TimelineDateBasisUnavailableError extends Error {
  constructor(
    message = "Placing the timeline by upload date is not available on this database yet"
  ) {
    super(message);
    this.name = "TimelineDateBasisUnavailableError";
  }
}
